import {
  createTooltip,
  deleteTooltip,
  getTooltips,
  updateTooltip,
} from '@/lib/adapters/tooltip-adapter';
import { tooltipsAtom } from '@/lib/atoms/atoms';
import { TooltipInput, TooltipItem } from '@/lib/types';
import { showNotification } from '@/lib/utils/showNotification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, Modal, Space, Table } from 'antd';
import { useSetAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const filterTooltips = (tooltips: TooltipItem[], search: string) => {
  const query = search.trim().toLowerCase();
  return query
    ? tooltips.filter(
        (tooltip) =>
          tooltip.key.toLowerCase().includes(query) ||
          (tooltip.value ?? '').toLowerCase().includes(query),
      )
    : tooltips;
};

export const TooltipManagement = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const setRuntimeTooltips = useSetAtom(tooltipsAtom);
  const [form] = Form.useForm<TooltipInput>();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<TooltipItem>();
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const notifyError = (error: any) =>
    showNotification(
      'error',
      error?.message || t('general.errors.unknown'),
    );

  const { data: tooltips = [], isFetching } = useQuery({
    queryKey: ['tooltips'],
    queryFn: () =>
      getTooltips().catch((error) => {
        notifyError(error);
        throw error;
      }),
  });

  const refreshTooltips = async () => {
    const current = await getTooltips();
    queryClient.setQueryData(['tooltips'], current);
    setRuntimeTooltips(current);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(undefined);
    form.resetFields();
  };

  const openModal = (tooltip?: TooltipItem) => {
    setEditing(tooltip);
    form.setFieldsValue(
      tooltip
        ? { key: tooltip.key, value: tooltip.value ?? '' }
        : { key: '', value: '' },
    );
    setModalOpen(true);
  };

  const saveTooltip = async (values: TooltipInput) => {
    setSaving(true);
    try {
      if (editing) {
        await updateTooltip(editing.id, values);
      } else {
        await createTooltip(values);
      }
      await refreshTooltips();
      showNotification(
        'success',
        t(editing ? 'settings.tooltips.updated' : 'settings.tooltips.created'),
      );
      closeModal();
    } catch (error: any) {
      notifyError(error);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (tooltip: TooltipItem) => {
    Modal.confirm({
      title: t('settings.tooltips.deleteTitle'),
      content: t('settings.tooltips.deleteContent', { key: tooltip.key }),
      okText: t('general.actions.delete'),
      cancelText: t('general.actions.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteTooltip(tooltip.id);
          await refreshTooltips();
          showNotification('success', t('settings.tooltips.deleted'));
        } catch (error: any) {
          notifyError(error);
          throw error;
        }
      },
    });
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Input.Search
          allowClear
          placeholder={t('settings.tooltips.search')}
          onChange={(event) => setSearch(event.target.value)}
          style={{ width: 360 }}
        />
        <Button type="primary" onClick={() => openModal()}>
          {t('settings.tooltips.create')}
        </Button>
      </Space>
      <Table<TooltipItem>
        rowKey="id"
        size="small"
        loading={isFetching}
        dataSource={filterTooltips(tooltips, search)}
        columns={[
          {
            title: t('settings.tooltips.key'),
            dataIndex: 'key',
            sorter: (a, b) => a.key.localeCompare(b.key),
          },
          {
            title: t('settings.tooltips.value'),
            dataIndex: 'value',
          },
          {
            key: 'actions',
            width: 100,
            render: (_, tooltip) => (
              <Space>
                <Button
                  type="link"
                  aria-label={t('settings.tooltips.edit')}
                  icon={<FontAwesomeIcon icon="pencil-alt" />}
                  onClick={() => openModal(tooltip)}
                />
                <Button
                  type="link"
                  danger
                  aria-label={t('general.actions.delete')}
                  icon={<FontAwesomeIcon icon={['far', 'trash-alt']} />}
                  onClick={() => confirmDelete(tooltip)}
                />
              </Space>
            ),
          },
        ]}
      />
      <Modal
        open={modalOpen}
        title={t(
          editing ? 'settings.tooltips.editTitle' : 'settings.tooltips.createTitle',
        )}
        okText={t('general.actions.save')}
        cancelText={t('general.actions.cancel')}
        confirmLoading={saving}
        onOk={() => form.submit()}
        onCancel={closeModal}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={saveTooltip}>
          <Form.Item
            name="key"
            label={t('settings.tooltips.key')}
            rules={[
              { required: true, message: t('settings.tooltips.keyRequired') },
              { max: 200, message: t('settings.tooltips.keyMax') },
            ]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="value"
            label={t('settings.tooltips.value')}
            rules={[{ max: 2000, message: t('settings.tooltips.valueMax') }]}
          >
            <Input.TextArea maxLength={2000} rows={6} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};
